import { useEffect, useState } from "react";
import { btn } from "../styles/shared";
import { apiFetch } from "../utils/apiClient";
import { APP_INFO, readVersionHistory, shortHash } from "../constants/appInfo";

export default function Admin({ debugLog, setDebugLog, showToast, logs, groceries, forceMigrate, systemAlerts = [], dismissAlert, clearAlerts, logoutApi, appInfo = APP_INFO }) {
  const [dataHealth, setDataHealth] = useState(null);

  useEffect(() => {
    apiFetch("/api/health")
      .then(r => r.json())
      .then(setDataHealth)
      .catch(() => setDataHealth({ ok:false, warnings:["Health check failed."] }));
  }, []);

  // ── Local data recovery ───────────────────────────────────────────────────
  const [scanResult, setScanResult] = useState(null); // null | { key, data, counts } | "none"
  const [pushing,    setPushing]    = useState(false);
  const [pushed,     setPushed]     = useState(false);

  // ── Backup controls ───────────────────────────────────────────────────────
  const lastBackup = localStorage.getItem("fitlog_last_backup") || "Never";
  const [backupStatus, setBackupStatus] = useState(null);
  const [downloading,  setDownloading]  = useState(false);
  const [deployInfo,   setDeployInfo]   = useState(null);
  const [versionHistory, setVersionHistory] = useState(() => readVersionHistory());
  const BACKUP_URL = process.env.NODE_ENV === "development" ? "http://localhost:8788/api/backup" : "/api/backup";
  const STATE_URL  = process.env.NODE_ENV === "development" ? "http://localhost:8788/api/state"  : "/api/state";
  const VERSION_URL = process.env.NODE_ENV === "development" ? "http://localhost:8788/api/version" : "/api/version";

  useEffect(() => {
    setVersionHistory(readVersionHistory());
    apiFetch(VERSION_URL)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDeployInfo(data); })
      .catch(() => {});
  }, [VERSION_URL]);

  function runBackupNow() {
    setBackupStatus("running");
    apiFetch(BACKUP_URL, { method: "POST" })
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          localStorage.setItem("fitlog_last_backup", d.date);
          setBackupStatus("Backed up to GitHub Gist on " + d.date);
          showToast("Backup complete");
        } else {
          setBackupStatus("Error: " + (d.error || "unknown"));
        }
      })
      .catch(e => setBackupStatus("Error: " + e.message));
  }

  function downloadBackup() {
    setDownloading(true);
    apiFetch(STATE_URL)
      .then(r => r.json())
      .then(data => {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a");
        a.href     = url;
        a.download = `fitlog_backup_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
        setDownloading(false);
      })
      .catch(() => { showToast("Download failed"); setDownloading(false); });
  }

  function scanLocalStorage() {
    const fitlogKeys = Object.keys(localStorage).filter(k => k.startsWith("fitlog"));
    for (const key of fitlogKeys) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || "{}");
        const counts = {
          "Meal logs":    data.logs?.length         || 0,
          "Groceries":    data.groceries?.length    || 0,
          "Workouts":     data.workouts?.length     || 0,
          "Weight entries": data.weightLog?.length  || 0,
          "Meal plans":   Object.keys(data.mealPlans || {}).length || (data.mealPlan ? 1 : 0),
          "Custom foods": data.customFoods?.length  || 0,
        };
        if (Object.values(counts).some(v => v > 0)) {
          setScanResult({ key, data, counts });
          return;
        }
      } catch {}
    }
    setScanResult("none");
  }

  async function pushToCloud() {
    if (!scanResult || scanResult === "none") return;
    setPushing(true);
    const ok = await forceMigrate(scanResult.data);
    setPushing(false);
    if (ok) {
      setPushed(true);
      showToast("Data recovered and saved to cloud");
    } else {
      showToast("Recovery stopped because a cloud write failed");
    }
  }
  const unreadAlerts = systemAlerts.filter(a => !a.read);
  const SEVERITY_COLOR = { critical: "#f87171", warning: "#fbbf24", info: "#60a5fa" };
  const SEVERITY_BG    = { critical: "rgba(239,68,68,0.08)", warning: "rgba(251,191,36,0.08)", info: "rgba(96,165,250,0.08)" };
  const SEVERITY_BORDER = { critical: "rgba(239,68,68,0.25)", warning: "rgba(251,191,36,0.25)", info: "rgba(96,165,250,0.2)" };
  const nativeInfo = appInfo.nativeInfo;
  const deployCommit = shortHash(deployInfo?.cloudflare?.commitSha);

  return (
<div style={{ display:"flex",flexDirection:"column",gap:20 }}>

  {dataHealth && (
    <div style={{ background:"rgba(34,211,238,0.05)",border:"1px solid rgba(34,211,238,0.2)",borderRadius:14,padding:"18px 20px" }}>
      <div style={{ display:"flex",justifyContent:"space-between",gap:12,alignItems:"center",flexWrap:"wrap" }}>
        <div>
          <div style={{ fontSize:13,fontWeight:600,color:"#22d3ee" }}>Database Health</div>
          <div style={{ fontSize:11,color:"#64748b",marginTop:3 }}>{dataHealth.checkedAt ? `Checked ${new Date(dataHealth.checkedAt).toLocaleString()}` : "Health check unavailable"}</div>
        </div>
        <div style={{ fontSize:12,fontWeight:700,color:dataHealth.ok?"#4ade80":"#f87171" }}>{dataHealth.ok ? "CONNECTED" : "ATTENTION NEEDED"}</div>
      </div>
      <div style={{ display:"flex",flexWrap:"wrap",gap:8,marginTop:14 }}>
        {Object.entries(dataHealth.tables || dataHealth.legacy || {}).map(([name,value]) => (
          <div key={name} style={{ background:"rgba(255,255,255,0.04)",borderRadius:7,padding:"6px 10px",fontSize:11 }}>
            <span style={{ color:"#777" }}>{name}: </span>
            <span style={{ color:"#ddd",fontFamily:"'Space Mono',monospace",fontWeight:700 }}>{typeof value==="object" ? value.count : value}</span>
          </div>
        ))}
      </div>
      {(dataHealth.warnings || []).map(warning => <div key={warning} style={{ color:"#fbbf24",fontSize:12,marginTop:8 }}>Warning: {warning}</div>)}
      <div style={{ color:dataHealth.latestBackup?.status==="verified"?"#4ade80":"#777",fontSize:12,marginTop:10 }}>
        Latest backup: {dataHealth.latestBackup?.status || "not recorded"}
      </div>
    </div>
  )}

  {/* ── System Alerts ── */}
  {systemAlerts.length > 0 && (
    <div style={{ display:"flex",flexDirection:"column",gap:0,borderRadius:14,overflow:"hidden",border:"1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ padding:"12px 18px",background:"rgba(255,255,255,0.03)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <span style={{ fontSize:13,fontWeight:600,color:"#ccc" }}>System Alerts</span>
          {unreadAlerts.length > 0 && (
            <span style={{ background:"rgba(239,68,68,0.15)",border:"1px solid rgba(239,68,68,0.3)",color:"#f87171",fontSize:11,fontFamily:"'Space Mono',monospace",borderRadius:6,padding:"2px 8px" }}>
              {unreadAlerts.length} unread
            </span>
          )}
        </div>
        {unreadAlerts.length > 0 && (
          <button onClick={clearAlerts} style={{ ...btn,padding:"5px 14px",fontSize:12,background:"rgba(255,255,255,0.05)",color:"#888",border:"1px solid rgba(255,255,255,0.1)" }}>
            Mark all read
          </button>
        )}
      </div>
      {systemAlerts.map(alert => (
        <div key={alert.id} style={{
          padding:"12px 18px",
          background: alert.read ? "rgba(255,255,255,0.01)" : SEVERITY_BG[alert.severity] || SEVERITY_BG.info,
          borderTop:"1px solid rgba(255,255,255,0.05)",
          display:"flex",alignItems:"flex-start",gap:12,
          opacity: alert.read ? 0.5 : 1,
        }}>
          <span style={{ fontSize:11,fontFamily:"'Space Mono',monospace",fontWeight:700,color:SEVERITY_COLOR[alert.severity]||"#aaa",marginTop:1,flexShrink:0,textTransform:"uppercase",letterSpacing:0.5 }}>
            {alert.severity}
          </span>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontSize:13,color:"#ddd",lineHeight:1.4 }}>{alert.message}</div>
            <div style={{ fontSize:11,color:"#555",marginTop:3,fontFamily:"'Space Mono',monospace" }}>
              {alert.source} · {alert.event} · {new Date(alert.timestamp).toLocaleString()}
            </div>
          </div>
          {!alert.read && (
            <button onClick={() => dismissAlert(alert.id)} style={{ ...btn,padding:"4px 10px",fontSize:11,background:"transparent",color:"#555",border:"1px solid rgba(255,255,255,0.1)",flexShrink:0 }}>
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  )}

  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12 }}>
    <div>
      <h1 style={{ fontSize:26,fontWeight:600,margin:0,letterSpacing:-0.5 }}>Admin</h1>
      <p style={{ color:"#999",margin:"4px 0 0",fontSize:14 }}>Import logs, errors, and diagnostics</p>
    </div>
    <div style={{ display:"flex",gap:8 }}>
      <button onClick={()=>navigator.clipboard?.writeText(debugLog.join("\n")).then(()=>showToast("✓ Copied!"))} style={{ ...btn,padding:"8px 16px",fontSize:12,background:"rgba(255,255,255,0.07)",color:"#aaa",border:"1px solid rgba(255,255,255,0.1)" }}>
        📋 Copy All Logs
      </button>
      <button onClick={()=>setDebugLog([])} style={{ ...btn,padding:"8px 16px",fontSize:12,background:"rgba(239,68,68,0.1)",color:"#f87171",border:"1px solid rgba(239,68,68,0.2)" }}>
        🗑 Clear Logs
      </button>
      {logoutApi && (
        <button onClick={logoutApi} style={{ ...btn,padding:"8px 16px",fontSize:12,background:"rgba(255,255,255,0.05)",color:"#888",border:"1px solid rgba(255,255,255,0.1)" }}>
          Lock App
        </button>
      )}
    </div>
  </div>

  {/* ── Data Recovery ── */}
  <div style={{ background:"rgba(99,102,241,0.06)",border:"1px solid rgba(129,140,248,0.18)",borderRadius:14,padding:"18px 20px" }}>
    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14,flexWrap:"wrap",marginBottom:14 }}>
      <div>
        <div style={{ fontSize:13,fontWeight:700,color:"#a5b4fc",marginBottom:3 }}>Ownership & Version</div>
        <div style={{ fontSize:12,color:"#999" }}>{APP_INFO.name} is registered here as {APP_INFO.ownerName}'s personal fitness system.</div>
      </div>
      <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#a5b4fc",background:"rgba(129,140,248,0.10)",border:"1px solid rgba(129,140,248,0.2)",borderRadius:7,padding:"5px 9px" }}>
        web v{APP_INFO.webVersion}
      </span>
    </div>
    <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(190px,1fr))",gap:10 }}>
      {[
        ["Owner", `${APP_INFO.ownerName} (@${APP_INFO.ownerHandle})`],
        ["Repository", APP_INFO.repository],
        ["Web Build", `v${APP_INFO.webVersion} · ${APP_INFO.releaseChannel}`],
        ["Cloudflare", deployCommit ? `${deployInfo.cloudflare.branch || "branch"} · ${deployCommit}` : "deployed bundle active"],
        ["Android Shell", nativeInfo?.version ? `v${nativeInfo.version} · build ${nativeInfo.buildNumber || "-"}` : "not running in native shell"],
        ["Shell Channel", nativeInfo?.channel || (appInfo.isNativeShell ? "native" : "browser/PWA")],
      ].map(([label, value]) => (
        <div key={label} style={{ background:"rgba(0,0,0,0.18)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"10px 12px" }}>
          <div style={{ fontSize:9,fontFamily:"'Space Mono',monospace",color:"#818cf8",textTransform:"uppercase",letterSpacing:1,marginBottom:5 }}>{label}</div>
          <div style={{ fontSize:12,color:"#ddd",wordBreak:"break-word" }}>{value}</div>
        </div>
      ))}
    </div>
    {versionHistory.length > 0 && (
      <div style={{ marginTop:14 }}>
        <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#818cf8",textTransform:"uppercase",letterSpacing:1,marginBottom:8 }}>Version History On This Device</div>
        <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
          {versionHistory.slice(0, 5).map((row, index) => (
            <div key={row.signature || index} style={{ display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:8,padding:"8px 10px",fontSize:11 }}>
              <span style={{ color:"#ccc" }}>
                web v{row.webVersion}{row.native?.version ? ` · android v${row.native.version} (${row.native.buildNumber || "-"})` : " · browser"}
              </span>
              <span style={{ color:"#666",fontFamily:"'Space Mono',monospace",whiteSpace:"nowrap" }}>{new Date(row.lastSeenAt).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>

  <div style={{ background:"rgba(249,115,22,0.05)",border:"1px solid rgba(249,115,22,0.2)",borderRadius:14,padding:"18px 20px" }}>
    <div style={{ fontSize:13,fontWeight:600,color:"#f97316",marginBottom:4 }}>Data Recovery</div>
    <div style={{ fontSize:12,color:"#999",marginBottom:14 }}>If your data isn't showing, scan this browser's local storage and push it to the cloud.</div>

    {!scanResult && (
      <button onClick={scanLocalStorage} style={{ ...btn,padding:"9px 20px",fontSize:13,background:"rgba(249,115,22,0.12)",color:"#f97316",border:"1px solid rgba(249,115,22,0.3)" }}>
        Scan Local Storage
      </button>
    )}

    {scanResult === "none" && (
      <div style={{ fontSize:13,color:"#f87171" }}>No fitlog data found in this browser's local storage.</div>
    )}

    {scanResult && scanResult !== "none" && !pushed && (
      <div>
        <div style={{ fontSize:12,color:"#888",marginBottom:10 }}>Found data under key <code style={{ color:"#f97316",fontSize:11 }}>{scanResult.key}</code>:</div>
        <div style={{ display:"flex",flexWrap:"wrap",gap:8,marginBottom:14 }}>
          {Object.entries(scanResult.counts).map(([k,v]) => (
            <div key={k} style={{ background:"rgba(255,255,255,0.05)",borderRadius:8,padding:"6px 12px",fontSize:12 }}>
              <span style={{ color:"#999",marginRight:6 }}>{k}:</span>
              <span style={{ color: v > 0 ? "#4ade80" : "#555", fontFamily:"'Space Mono',monospace",fontWeight:700 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex",gap:10 }}>
          <button onClick={pushToCloud} disabled={pushing} style={{ ...btn,padding:"9px 20px",fontSize:13,background:"rgba(74,222,128,0.12)",color:"#4ade80",border:"1px solid rgba(74,222,128,0.3)",opacity:pushing?0.6:1 }}>
            {pushing ? "Pushing to cloud…" : "Push to Cloud"}
          </button>
          <button onClick={() => setScanResult(null)} style={{ ...btn,padding:"9px 16px",fontSize:13,background:"rgba(255,255,255,0.05)",color:"#888",border:"1px solid rgba(255,255,255,0.1)" }}>
            Cancel
          </button>
        </div>
      </div>
    )}

    {pushed && (
      <div style={{ fontSize:13,color:"#4ade80" }}>✓ Data recovered and saved to cloud. Reload the app to see your data.</div>
    )}
  </div>

  {/* ── Backup & Export ── */}
  <div style={{ background:"rgba(52,211,153,0.05)",border:"1px solid rgba(52,211,153,0.2)",borderRadius:14,padding:"18px 20px" }}>
    <div style={{ fontSize:13,fontWeight:600,color:"#34d399",marginBottom:4 }}>Backup & Export</div>
    <div style={{ fontSize:12,color:"#999",marginBottom:14 }}>
      Last off-site backup: <span style={{ color:"#ccc",fontFamily:"'Space Mono',monospace" }}>{lastBackup}</span>
      <span style={{ marginLeft:12,color:"#555",fontSize:11 }}>Verified backup runs every 3 hours · 30-day rolling backup kept in R2</span>
    </div>
    <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
      <button onClick={runBackupNow} disabled={backupStatus==="running"} style={{ ...btn,padding:"9px 20px",fontSize:13,background:"rgba(52,211,153,0.1)",color:"#34d399",border:"1px solid rgba(52,211,153,0.3)",opacity:backupStatus==="running"?0.6:1 }}>
        {backupStatus==="running" ? "Backing up…" : "Back Up to GitHub Now"}
      </button>
      <button onClick={downloadBackup} disabled={downloading} style={{ ...btn,padding:"9px 20px",fontSize:13,background:"rgba(255,255,255,0.06)",color:"#ccc",border:"1px solid rgba(255,255,255,0.1)",opacity:downloading?0.6:1 }}>
        {downloading ? "Downloading…" : "Download JSON Backup"}
      </button>
    </div>
    {backupStatus && backupStatus !== "running" && (
      <div style={{ marginTop:10,fontSize:12,color: backupStatus.startsWith("Error") ? "#f87171" : "#4ade80" }}>
        {backupStatus}
      </div>
    )}
    <div style={{ marginTop:14,fontSize:11,color:"#555" }}>
      Requires <code style={{ color:"#f97316" }}>GITHUB_BACKUP_TOKEN</code> secret in Cloudflare Pages → Settings → Environment Variables
    </div>
  </div>

  {/* Status summary */}
  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12 }}>
    {[
      { label:"Log Entries",   val:debugLog.filter(l=>l.trim()).length,                          color:"#888"    },
      { label:"Errors",        val:debugLog.filter(l=>l.startsWith("❌")||l.startsWith("✗")).length, color:"#f87171" },
      { label:"Warnings",      val:debugLog.filter(l=>l.startsWith("⚠")).length,                color:"#fbbf24" },
      { label:"Successes",     val:debugLog.filter(l=>l.startsWith("✅")).length,                color:"#4ade80" },
    ].map(c=>(
      <div key={c.label} style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"14px 18px" }}>
        <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#999",textTransform:"uppercase",letterSpacing:1.2,marginBottom:8 }}>{c.label}</div>
        <div style={{ fontSize:28,fontWeight:700,fontFamily:"'Space Mono',monospace",color:c.color }}>{c.val}</div>
      </div>
    ))}
  </div>

  {/* Log panel */}
  <div style={{ background:"#030308",border:"1px solid rgba(255,255,255,0.08)",borderRadius:14,overflow:"hidden" }}>
    <div style={{ padding:"12px 18px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",gap:8 }}>
      <span style={{ width:10,height:10,borderRadius:"50%",background:"#ef4444",display:"inline-block" }}/>
      <span style={{ width:10,height:10,borderRadius:"50%",background:"#f59e0b",display:"inline-block" }}/>
      <span style={{ width:10,height:10,borderRadius:"50%",background:"#22c55e",display:"inline-block" }}/>
      <span style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#888",marginLeft:8 }}>fitlog.import.log</span>
    </div>
    <div style={{ padding:"16px 20px",maxHeight:520,overflowY:"auto",fontFamily:"'Space Mono',monospace",fontSize:12,lineHeight:1.8 }}>
      {debugLog.length===0 ? (
        <div style={{ color:"#888",fontStyle:"normal" }}>No log entries yet. Import a meal plan or grocery list to see diagnostics here.</div>
      ) : (
        debugLog.map((line,i)=> {
          const color = line.startsWith("✅") ? "#4ade80"
                      : line.startsWith("✓")  ? "#4ade80"
                      : line.startsWith("❌") ? "#f87171"
                      : line.startsWith("✗")  ? "#f87171"
                      : line.startsWith("⚠")  ? "#fbbf24"
                      : line.startsWith("📝") ? "#94a3b8"
                      : line.startsWith("→")  ? "#a78bfa"
                      : line.startsWith("──") ? "#333"
                      : "#aaa";
          const isSeparator = line.startsWith("──");
          return (
            <div key={i} style={{
              color, whiteSpace:"pre-wrap", wordBreak:"break-all",
              borderTop: isSeparator ? "1px solid rgba(255,255,255,0.06)" : "none",
              paddingTop: isSeparator ? 10 : 0,
              marginTop:  isSeparator ? 6 : 0,
              opacity:    isSeparator ? 0.5 : 1,
              fontSize:   isSeparator ? 10 : 12,
            }}>{line||" "}</div>
          );
        })
      )}
    </div>
  </div>

  {/* App info */}
  <div style={{ background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"14px 18px" }}>
    <div style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#999",textTransform:"uppercase",letterSpacing:1.2,marginBottom:10 }}>App State</div>
    <div style={{ display:"flex",flexWrap:"wrap",gap:10 }}>
      {[
        ["Meal Logs",   logs.length+" entries"],
        ["Groceries",   groceries.length+" items"],
      ].map(([k,v])=>(
        <div key={k} style={{ background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"7px 14px",fontSize:12 }}>
          <span style={{ color:"#999",marginRight:6 }}>{k}:</span>
          <span style={{ color:"#ccc",fontFamily:"'Space Mono',monospace" }}>{v}</span>
        </div>
      ))}
    </div>
  </div>
</div>
  );
}
